import React, { useState, useRef, useEffect } from 'react'
    import { motion } from 'framer-motion'
    import * as FiIcons from 'react-icons/fi'
    import SafeIcon from '../common/SafeIcon'
    
    const {
      FiSave, FiX, FiRotateCw, FiRefreshCw, FiMaximize2, FiMinimize2, 
      FiMove, FiCrop, FiCamera, FiImage, FiAperture, FiCheck, FiSwitchCamera
    } = FiIcons
    
    const ImageCropper = ({ imageUrl, onSave, onCancel, isCameraMode }) => {
      const containerRef = useRef(null)
      const imageRef = useRef(null)
      const videoRef = useRef(null)
      const [image, setImage] = useState(null)
      const [imageLoaded, setImageLoaded] = useState(false)
      const [isMobile, setIsMobile] = useState(false)
      const [showCamera, setShowCamera] = useState(isCameraMode || false)
      const [stream, setStream] = useState(null)
      const [devices, setDevices] = useState([])
      const [selectedDevice, setSelectedDevice] = useState(null)
      const [cameraError, setCameraError] = useState(null)
      const [facingMode, setFacingMode] = useState('user') // 'user' for front, 'environment' for back
      const [capturedImage, setCapturedImage] = useState(null) // For storing captured image before cropping
    
      // Missing state definitions
      const [imageDisplayBounds, setImageDisplayBounds] = useState({ left: 0, top: 0, width: 0, height: 0 });
      const [cropArea, setCropArea] = useState({ x: 0, y: 0, size: 200 });
      const [isDragging, setIsDragging] = useState(false);
      const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
      const [lastTouchDistance, setLastTouchDistance] = useState(0);
    
      // Detect mobile device
      useEffect(() => {
        const checkMobile = () => {
          const userAgent = navigator.userAgent || navigator.vendor || window.opera
          const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase())
          setIsMobile(isMobileDevice)
          return isMobileDevice
        }
        checkMobile()
      }, [])
    
      // CRITICAL: Prevent browser zoom on mobile
      useEffect(() => {
        if (!isMobile) return
    
        const preventZoom = (e) => {
          if (e.touches && e.touches.length > 1) {
            e.preventDefault()
          }
        }
    
        const preventGestureZoom = (e) => {
          e.preventDefault()
        }
    
        document.addEventListener('touchstart', preventZoom, { passive: false })
        document.addEventListener('touchmove', preventZoom, { passive: false })
        document.addEventListener('touchend', preventZoom, { passive: false })
        document.addEventListener('gesturestart', preventGestureZoom, { passive: false })
        document.addEventListener('gesturechange', preventGestureZoom, { passive: false })
        document.addEventListener('gestureend', preventGestureZoom, { passive: false })
        document.addEventListener('dblclick', preventGestureZoom, { passive: false })
    
        return () => {
          document.removeEventListener('touchstart', preventZoom)
          document.removeEventListener('touchmove', preventZoom)
          document.removeEventListener('touchend', preventZoom)
          document.removeEventListener('gesturestart', preventGestureZoom)
          document.removeEventListener('gesturechange', preventGestureZoom)
          document.removeEventListener('gestureend', preventGestureZoom)
          document.removeEventListener('dblclick', preventGestureZoom)
        }
      }, [isMobile])
    
      // Load image and set up container
      useEffect(() => {
        if (imageUrl && imageUrl !== 'camera') {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.onload = () => {
            setImage(img)
            setImageLoaded(true)
            setTimeout(() => {
              updateImageBounds()
              initializeCropArea()
            }, 200)
          }
          img.src = imageUrl
        } else if (imageUrl === 'camera') {
          setShowCamera(true)
          startCamera()
        }
      }, [imageUrl])
    
      // Get available cameras
      useEffect(() => {
        const getCameras = async () => {
          try {
            // Request camera permission first
            await navigator.mediaDevices.getUserMedia({ video: true })
            const devices = await navigator.mediaDevices.enumerateDevices()
            const videoDevices = devices.filter(device => device.kind === 'videoinput')
            setDevices(videoDevices)
            console.log('Available cameras:', videoDevices)
    
            // Set initial camera if available
            if (videoDevices.length > 0) {
              // Prefer the front camera first
              const frontCamera = videoDevices.find(device => 
                device.label.toLowerCase().includes('front') || 
                device.label.toLowerCase().includes('face') ||
                device.label.toLowerCase().includes('user')
              )
              if (frontCamera) {
                setSelectedDevice(frontCamera.deviceId)
              } else {
                setSelectedDevice(videoDevices[0].deviceId)
              }
            }
          } catch (err) {
            console.error('Error getting cameras:', err)
            setCameraError('Could not access cameras. Please ensure you have granted camera permissions.')
          }
        }
    
        if (showCamera) {
          getCameras()
        }
      }, [showCamera])
    
      // Cleanup camera stream on unmount
      useEffect(() => {
        return () => {
          if (stream) {
            stream.getTracks().forEach(track => track.stop())
          }
        }
      }, [stream])
    
      // Update image bounds when window resizes
      useEffect(() => {
        const handleResize = () => {
          if (imageLoaded) {
            updateImageBounds()
          }
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
      }, [imageLoaded])
    
      // Calculate the actual display bounds of the image within its container
      const updateImageBounds = () => {
        if (!imageRef.current || !image) return
    
        const imgElement = imageRef.current
        const imgRect = imgElement.getBoundingClientRect()
        const containerRect = containerRef.current.getBoundingClientRect()
    
        const imgAspectRatio = image.width / image.height
        const displayAspectRatio = imgRect.width / imgRect.height
    
        let displayWidth, displayHeight, displayLeft, displayTop
    
        if (imgAspectRatio > displayAspectRatio) {
          displayWidth = imgRect.width
          displayHeight = imgRect.width / imgAspectRatio
          displayLeft = imgRect.left - containerRect.left
          displayTop = imgRect.top - containerRect.top + (imgRect.height - displayHeight) / 2
        } else {
          displayWidth = imgRect.height * imgAspectRatio
          displayHeight = imgRect.height
          displayLeft = imgRect.left - containerRect.left + (imgRect.width - displayWidth) / 2
          displayTop = imgRect.top - containerRect.top
        }
    
        setImageDisplayBounds({
          left: displayLeft,
          top: displayTop,
          width: displayWidth,
          height: displayHeight
        })
      }
    
      // Initialize crop area in the center of the displayed image
      const initializeCropArea = () => {
        if (!imageRef.current || !image) return
    
        updateImageBounds()
        setTimeout(() => {
          const cropSize = Math.min(imageDisplayBounds.width, imageDisplayBounds.height) * 0.6
          setCropArea({
            x: (imageDisplayBounds.width - cropSize) / 2,
            y: (imageDisplayBounds.height - cropSize) / 2,
            size: cropSize
          })
        }, 100)
      }
    
      // Get touch distance for pinch zoom
      const getTouchDistance = (touches) => {
        const dx = touches[0].clientX - touches[1].clientX
        const dy = touches[0].clientY - touches[1].clientY
        return Math.sqrt(dx * dx + dy * dy)
      }
    
      // Convert container coordinates to image-relative coordinates
      const containerToImageCoords = (containerX, containerY) => {
        return {
          x: containerX - imageDisplayBounds.left,
          y: containerY - imageDisplayBounds.top
        }
      }
    
      // Handle touch start
      const handleTouchStart = (e) => {
        e.preventDefault()
        e.stopPropagation()
    
        if (e.touches.length === 2) {
          const distance = getTouchDistance(e.touches)
          setLastTouchDistance(distance)
          setIsDragging(false)
        } else if (e.touches.length === 1) {
          const touch = e.touches[0]
          const rect = containerRef.current.getBoundingClientRect()
          const containerX = touch.clientX - rect.left
          const containerY = touch.clientY - rect.top
          handleInteractionStart(containerX, containerY)
        }
      }
    
      // Handle touch move
      const handleTouchMove = (e) => {
        e.preventDefault()
        e.stopPropagation()
    
        if (e.touches.length === 2 && lastTouchDistance > 0) {
          const distance = getTouchDistance(e.touches)
          const sizeDelta = (distance - lastTouchDistance) * 0.8
    
          setCropArea(prev => {
            const newSize = Math.max(50, Math.min(Math.min(imageDisplayBounds.width, imageDisplayBounds.height), prev.size + sizeDelta))
            const maxX = imageDisplayBounds.width - newSize
            const maxY = imageDisplayBounds.height - newSize
    
            return {
              x: Math.max(0, Math.min(maxX, prev.x)),
              y: Math.max(0, Math.min(maxY, prev.y)),
              size: newSize
            }
          })
    
          setLastTouchDistance(distance)
        } else if (e.touches.length === 1 && isDragging) {
          const touch = e.touches[0]
          const rect = containerRef.current.getBoundingClientRect()
          const containerX = touch.clientX - rect.left
          const containerY = touch.clientY - rect.top
          handleInteractionMove(containerX, containerY)
        }
      }
    
      // Handle touch end
      const handleTouchEnd = (e) => {
        e.preventDefault()
        e.stopPropagation()
    
        if (e.touches.length === 0) {
          setIsDragging(false)
          setLastTouchDistance(0)
        } else if (e.touches.length === 1) {
          setLastTouchDistance(0)
        }
      }
    
      // Handle mouse events (for desktop)
      const handleMouseDown = (e) => {
        const rect = containerRef.current.getBoundingClientRect()
        const containerX = e.clientX - rect.left
        const containerY = e.clientY - rect.top
        handleInteractionStart(containerX, containerY)
      }
    
      const handleMouseMove = (e) => {
        if (!isDragging) return
    
        const rect = containerRef.current.getBoundingClientRect()
        const containerX = e.clientX - rect.left
        const containerY = e.clientY - rect.top
        handleInteractionMove(containerX, containerY)
      }
    
      const handleMouseUp = () => {
        setIsDragging(false)
      }
    
      // Wheel to resize crop area (desktop only)
      const handleWheel = (e) => {
        if (isMobile) return
        e.preventDefault()
    
        const sizeDelta = -e.deltaY * 0.5
    
        setCropArea(prev => {
          const newSize = Math.max(50, Math.min(Math.min(imageDisplayBounds.width, imageDisplayBounds.height), prev.size + sizeDelta))
          const maxX = imageDisplayBounds.width - newSize
          const maxY = imageDisplayBounds.height - newSize
    
          return {
            x: Math.max(0, Math.min(maxX, prev.x)),
            y: Math.max(0, Math.min(maxY, prev.y)),
            size: newSize
          }
        })
      }
    
      // Unified interaction start handler
      const handleInteractionStart = (containerX, containerY) => {
        const imageCoords = containerToImageCoords(containerX, containerY)
    
        if (
          imageCoords.x >= cropArea.x &&
          imageCoords.x <= cropArea.x + cropArea.size &&
          imageCoords.y >= cropArea.y &&
          imageCoords.y <= cropArea.y + cropArea.size
        ) {
          setDragStart({
            x: imageCoords.x - cropArea.x,
            y: imageCoords.y - cropArea.y
          })
          setIsDragging(true)
        }
      }
    
      // Unified interaction move handler
      const handleInteractionMove = (containerX, containerY) => {
        if (!isDragging) return
    
        const imageCoords = containerToImageCoords(containerX, containerY)
        const newX = imageCoords.x - dragStart.x
        const newY = imageCoords.y - dragStart.y
    
        const maxX = imageDisplayBounds.width - cropArea.size
        const maxY = imageDisplayBounds.height - cropArea.size
    
        setCropArea(prev => ({
          ...prev,
          x: Math.max(0, Math.min(maxX, newX)),
          y: Math.max(0, Math.min(maxY, newY))
        }))
      }
    
      // Reset crop area
      const handleReset = () => {
        if (imageDisplayBounds.width && imageDisplayBounds.height) {
          const cropSize = Math.min(imageDisplayBounds.width, imageDisplayBounds.height) * 0.6
          setCropArea({
            x: (imageDisplayBounds.width - cropSize) / 2,
            y: (imageDisplayBounds.height - cropSize) / 2,
            size: cropSize
          })
        }
      }
    
      // Save cropped image
      const handleSave = () => {
        if (!image && !showCamera && !capturedImage) return
    
        if (image && (capturedImage || imageUrl)) {
          // Crop the displayed image
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          const outputSize = 400
          canvas.width = outputSize
          canvas.height = outputSize
    
          const scaleX = image.width / imageDisplayBounds.width
          const scaleY = image.height / imageDisplayBounds.height
    
          const sourceX = cropArea.x * scaleX
          const sourceY = cropArea.y * scaleY
          const sourceSize = cropArea.size * Math.min(scaleX, scaleY)
    
          const finalSourceX = Math.max(0, Math.min(sourceX, image.width - 1))
          const finalSourceY = Math.max(0, Math.min(sourceY, image.height - 1))
          const finalSourceSize = Math.min(
            sourceSize,
            image.width - finalSourceX,
            image.height - finalSourceY
          )
    
          ctx.drawImage(
            image,
            finalSourceX,
            finalSourceY,
            finalSourceSize,
            finalSourceSize,
            0,
            0,
            outputSize,
            outputSize
          )
    
          canvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], `cropped_image_${Date.now()}.jpg`, { type: 'image/jpeg' })
              onSave(file, URL.createObjectURL(blob))
            }
          }, 'image/jpeg', 0.9)
    
          return
        }
    
        if (showCamera && videoRef.current) {
          // Capture photo from camera
          const video = videoRef.current
          const canvas = document.createElement('canvas')
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          const ctx = canvas.getContext('2d')
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    
          // Store the captured image for cropping
          const capturedImageUrl = canvas.toDataURL('image/jpeg')
          setCapturedImage(capturedImageUrl)
    
          // Load the captured image for cropping
          const img = new Image()
          img.onload = () => {
            setImage(img)
            setImageLoaded(true)
            setTimeout(() => {
              updateImageBounds()
              initializeCropArea()
            }, 200)
          }
          img.src = capturedImageUrl
    
          return
        }
      }
    
      // Calculate crop overlay position
      const getCropOverlayStyle = () => {
        return {
          left: imageDisplayBounds.left + cropArea.x,
          top: imageDisplayBounds.top + cropArea.y,
          width: cropArea.size,
          height: cropArea.size,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)'
        }
      }
    
      // Start camera
      const startCamera = async (deviceId = null) => {
        try {
          setCameraError(null)
    
          // Stop any existing stream
          if (stream) {
            stream.getTracks().forEach(track => track.stop())
            setStream(null)
          }
    
          const constraints = {
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          }
    
          // Use specific device if provided or selected
          if (deviceId || selectedDevice) {
            constraints.video.deviceId = { exact: deviceId || selectedDevice }
          } else if (facingMode) {
            constraints.video.facingMode = facingMode
          }
    
          console.log('Starting camera with constraints:', constraints)
          const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
          setStream(mediaStream)
          setShowCamera(true)
    
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream
          }
        } catch (err) {
          console.error('Error accessing camera:', err)
          setCameraError('Could not access camera. Please ensure you have granted camera permissions.')
        }
      }
    
      // Stop camera
      const stopCamera = () => {
        if (stream) {
          stream.getTracks().forEach(track => track.stop())
          setStream(null)
        }
        setShowCamera(false)
        setCapturedImage(null)
      }
    
      // Retake photo
      const retakePhoto = () => {
        setCapturedImage(null)
        setImage(null)
        setImageLoaded(false)
      }
    
      // Switch camera (toggle between front and back)
      const switchCamera = async () => {
        try {
          const newFacingMode = facingMode === 'user' ? 'environment' : 'user'
          setFacingMode(newFacingMode)
    
          // Stop current stream
          if (stream) {
            stream.getTracks().forEach(track => track.stop())
          }
    
          // Start new stream with updated facing mode
          const constraints = {
            video: {
              facingMode: newFacingMode,
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          }
    
          const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
          setStream(mediaStream)
    
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream
          }
        } catch (err) {
          console.error('Error switching camera:', err)
          // Fallback to device selection if facingMode doesn't work
          selectNextDevice()
        }
      }
    
      // Select next available camera device
      const selectNextDevice = () => {
        if (devices.length <= 1) return
    
        const currentIndex = devices.findIndex(d => d.deviceId === selectedDevice)
        const nextIndex = (currentIndex + 1) % devices.length
        const nextDevice = devices[nextIndex]
    
        console.log('Switching to device:', nextDevice.label)
        setSelectedDevice(nextDevice.deviceId)
    
        // Restart camera with new device
        setTimeout(() => {
          startCamera(nextDevice.deviceId)
        }, 100)
      }
    
      // Handle device selection from dropdown
      const handleDeviceSelect = (deviceId) => {
        setSelectedDevice(deviceId)
    
        // Restart camera with selected device
        setTimeout(() => {
          startCamera(deviceId)
        }, 100)
      }
    
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-95 flex flex-col z-50"
          style={{
            height: isMobile ? '100dvh' : '100vh',
            maxHeight: isMobile ? '100dvh' : '100vh',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none'
          }}
        >
          {/* Header */}
          <div className="bg-white px-4 py-3 flex items-center justify-between flex-shrink-0 safe-area-inset-top">
            <h3 className="text-lg font-semibold text-gray-800">
              {capturedImage ? 'Crop Photo' : showCamera ? 'Take Photo' : 'Crop Image'}
            </h3>
            <button
              onClick={() => {
                stopCamera()
                onCancel()
              }}
              className="text-gray-500 hover:text-gray-700 p-1"
            >
              <SafeIcon icon={FiX} size={24} />
            </button>
          </div>
    
          {/* Main content */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Image container */}
            <div
              ref={containerRef}
              className="flex-1 relative bg-gray-900 overflow-hidden touch-none select-none flex items-center justify-center p-4"
              style={{
                minHeight: isMobile ? '200px' : '300px',
                touchAction: 'none',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none'
              }}
              onMouseDown={!isMobile && imageLoaded && image ? handleMouseDown : undefined}
              onMouseMove={!isMobile && imageLoaded && image ? handleMouseMove : undefined}
              onMouseUp={!isMobile && imageLoaded && image ? handleMouseUp : undefined}
              onMouseLeave={!isMobile && imageLoaded && image ? handleMouseUp : undefined}
              onWheel={!isMobile && imageLoaded && image ? handleWheel : undefined}
              onTouchStart={isMobile && imageLoaded && image ? handleTouchStart : undefined}
              onTouchMove={isMobile && imageLoaded && image ? handleTouchMove : undefined}
              onTouchEnd={isMobile && imageLoaded && image ? handleTouchEnd : undefined}
            >
              {showCamera && !capturedImage ? (
                // Camera view
                <div className="w-full h-full relative">
                  {cameraError ? (
                    <div className="text-white text-center flex flex-col items-center justify-center h-full">
                      <SafeIcon icon={FiCamera} className="text-4xl mb-4" />
                      <p className="mb-4">{cameraError}</p>
                      <button
                        onClick={startCamera}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                      >
                        Try Again
                      </button>
                    </div>
                  ) : (
                    <>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />
    
                      {/* Camera controls */}
                      <div className="absolute bottom-6 left-0 right-0 flex justify-center space-x-8">
                        <button
                          onClick={handleSave} // Changed to handleSave to capture image first
                          className="bg-white bg-opacity-90 text-black p-4 rounded-full hover:bg-opacity-100 transition-all border-4 border-white"
                        >
                          <SafeIcon icon={FiCamera} size={32} />
                        </button>
                      </div>
    
                      {/* Camera indicator and device selector */}
                      <div className="absolute top-4 left-4 flex items-center space-x-2">
                        <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full flex items-center">
                          <SafeIcon icon={FiAperture} className="mr-2" />
                          <span>Camera</span>
                        </div>
    
                        {/* Camera device selector dropdown - only show if multiple devices */}
                        {devices.length > 1 && (
                          <div className="relative">
                            <select
                              value={selectedDevice || ''}
                              onChange={(e) => handleDeviceSelect(e.target.value)}
                              className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm"
                            >
                              {devices.map((device, index) => (
                                <option key={device.deviceId} value={device.deviceId}>
                                  {device.label || `Camera ${index + 1}`}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : imageLoaded && image ? (
                // Image cropping view
                <>
                  <img
                    ref={imageRef}
                    src={capturedImage || imageUrl}
                    alt="Crop preview"
                    className="max-w-full max-h-full object-contain"
                    draggable={false}
                    onLoad={updateImageBounds}
                    style={{
                      touchAction: 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none'
                    }}
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      touchAction: 'none',
                      userSelect: 'none'
                    }}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-60" />
                    <div
                      className="absolute border-2 border-blue-500 bg-transparent pointer-events-auto cursor-move"
                      style={{
                        ...getCropOverlayStyle(),
                        touchAction: 'none',
                        userSelect: 'none'
                      }}
                    >
                      {!isMobile && (
                        <>
                          <div className="absolute -top-1 -left-1 w-3 h-3 bg-blue-500 border border-white" />
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 border border-white" />
                          <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-blue-500 border border-white" />
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 border border-white" />
                        </>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-4 h-0.5 bg-white opacity-75" />
                        <div className="absolute w-0.5 h-4 bg-white opacity-75" />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-white text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p>Loading...</p>
                </div>
              )}
    
              {imageLoaded && image && !isMobile && (
                <div className="absolute top-4 left-4 right-4 bg-black bg-opacity-75 text-white p-3 rounded-lg text-sm z-10">
                  <p className="text-center">
                    🖱️ <strong>Drag:</strong> Move crop area • <strong>Scroll:</strong> Resize • <strong>Blue square:</strong> Final crop
                  </p>
                </div>
              )}
    
              {imageLoaded && image && isMobile && (
                <div className="absolute top-2 left-2 right-2 bg-black bg-opacity-75 text-white p-2 rounded-lg text-xs z-10">
                  <p className="text-center">
                    📱 <strong>Drag:</strong> Move crop • <strong>Pinch:</strong> Resize crop area
                  </p>
                </div>
              )}
            </div>
    
            {/* Control bar */}
            <div
              className="bg-white flex-shrink-0 safe-area-inset-bottom"
              style={{
                paddingTop: '1rem',
                paddingBottom: isMobile ? '1.5rem' : '1rem',
                paddingLeft: '1rem',
                paddingRight: '1rem'
              }}
            >
              <div className="flex items-center justify-between max-w-4xl mx-auto">
                <div className="flex items-center space-x-2">
                  {showCamera && !capturedImage ? (
                    <SafeIcon icon={FiCamera} className="text-gray-500" />
                  ) : (
                    <>
                      <SafeIcon icon={FiCrop} className="text-gray-500" />
                      <span className="text-sm text-gray-600">{Math.round(cropArea.size)}px</span>
                    </>
                  )}
                </div>
    
                <div className="flex items-center space-x-2">
                  {imageLoaded && image && !isMobile && (
                    <button
                      onClick={handleReset}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center space-x-2"
                      title="Reset crop area"
                    >
                      <SafeIcon icon={FiRefreshCw} />
                      <span className="hidden sm:inline">Reset</span>
                    </button>
                  )}
    
                  {capturedImage && (
                    <button
                      onClick={retakePhoto}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center space-x-2"
                      title="Retake photo"
                    >
                      <SafeIcon icon={FiRefreshCw} />
                      <span className="hidden sm:inline">Retake</span>
                    </button>
                  )}
                </div>
    
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      stopCamera()
                      onCancel()
                    }}
                    className={`${isMobile ? 'px-4 py-3' : 'px-6 py-2'} bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-medium`}
                  >
                    Cancel
                  </button>
    
                  <button
                    onClick={handleSave}
                    disabled={!imageLoaded && (!showCamera || capturedImage)}
                    className={`${isMobile ? 'px-4 py-3' : 'px-6 py-2'} bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2 font-medium`}
                  >
                    <SafeIcon icon={showCamera && !capturedImage ? FiCamera : FiSave} />
                    <span>{showCamera && !capturedImage ? 'Capture' : 'Save'}</span>
                  </button>
                </div>
              </div>
    
              {imageLoaded && image && isMobile && (
                <div className="mt-2 text-center">
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm flex items-center space-x-1 mx-auto"
                    title="Reset crop area"
                  >
                    <SafeIcon icon={FiRefreshCw} size={14} />
                    <span>Reset Crop</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )
    }
    
    export default ImageCropper